import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Phone, ArrowLeft, Loader2, ShieldCheck, RefreshCw } from "lucide-react";
import { auth, RecaptchaVerifier, signInWithPhoneNumber } from "@/lib/firebase";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

export default function PhoneAuthModal({ 
  isOpen, 
  onClose, 
  // Registration data (optional - for registration flow)
  registrationData = null,
  onSuccess = null 
}) {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "admin"]);

  const [step, setStep] = useState("phone"); // phone, otp
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const recaptchaContainerRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);
  
  // Pre-fill phone number from registration data
  useEffect(() => {
    if (registrationData?.phone) {
      // Remove leading 0 if present
      let phone = registrationData.phone.replace(/\D/g, "");
      if (phone.startsWith("0")) {
        phone = phone.slice(1);
      }
      setPhoneNumber(phone.slice(0, 10));
    }
  }, [registrationData]);

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          console.log("Recaptcha cleanup error:", e);
        }
      }
    };
  }, []);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, "");
    
    // If starts with 0, keep as is for Turkish format
    // Otherwise add country code
    if (digits.startsWith("90")) {
      return "+" + digits;
    } else if (digits.startsWith("0")) {
      return "+9" + digits;
    } else if (digits.length === 10) {
      return "+90" + digits;
    }
    return "+90" + digits;
  };

  const setupRecaptcha = () => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch (e) {
        console.log("Clear error:", e);
      }
    }

    recaptchaVerifierRef.current = new RecaptchaVerifier(auth, "recaptcha-container", {
      size: "invisible",
      callback: () => {
        console.log("Recaptcha solved");
      },
      "expired-callback": () => {
        toast.error(t("common:toast_recaptchaSuresiDolduLutfenTekrar"));
        setLoading(false);
      }
    });
  };

  const handleSendOtp = async () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      toast.error(t("common:toast_lutfenGecerliBirTelefonNumarasi"));
      return;
    }

    setLoading(true);
    
    try {
      setupRecaptcha();
      
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log("Sending OTP to:", formattedPhone);
      
      const confirmation = await signInWithPhoneNumber(
        auth,
        formattedPhone,
        recaptchaVerifierRef.current
      );
      
      setConfirmationResult(confirmation);
      setStep("otp");
      setResendCooldown(60);
      toast.success(t("admin:toast_dogrulamaKoduGonderildi"));
    } catch (error) {
      console.error("OTP Error:", error);
      
      let errorMessage = t("common:smsSendFailed");
      
      if (error.code === "auth/invalid-phone-number") {
        errorMessage = t("common:invalidPhoneFormat");
      } else if (error.code === "auth/too-many-requests") {
        errorMessage = t("common:tooManyAttempts");
      } else if (error.code === "auth/quota-exceeded") {
        errorMessage = t("common:smsQuotaFull");
      }
      
      toast.error(errorMessage);
      
      // Reset recaptcha
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (e) {
          console.log("Recaptcha clear error:", e);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      toast.error(t("admin:toast_lutfen6HaneliDogrulamaKodunu"));
      return;
    }

    setLoading(true);

    try {
      const result = await confirmationResult.confirm(otp);
      const user = result.user;
      
      // Get Firebase ID token
      const idToken = await user.getIdToken();
      
      // Send to backend for verification and user creation/login
      let response;
      let data = {};
      
      try {
        // Build request body - include registration data if provided
        const requestBody = {
          id_token: idToken,
          phone_number: user.phoneNumber,
        };
        
        // Add registration data if this is a registration flow
        if (registrationData) {
          requestBody.name = registrationData.name;
          requestBody.surname = registrationData.surname;
          requestBody.email = registrationData.email || null;
          requestBody.gender = registrationData.gender || null;
          requestBody.birth_date = registrationData.birth_date || null;
          requestBody.role = registrationData.role || "member";
          requestBody.known_branches = registrationData.known_branches || [];
          requestBody.password = registrationData.password || null;
        }
        
        response = await fetch(`${API}/auth/firebase/phone`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(requestBody),
        });

        // Parse response as text first to avoid "body stream already read" error
        const responseText = await response.text();
        try {
          data = responseText ? JSON.parse(responseText) : {};
        } catch (parseError) {
          console.error("JSON parse error:", parseError, "Response:", responseText);
          // Continue with empty data, will handle error below
        }
        
        // Check for error response
        if (!response.ok) {
          const errorMsg = data.detail || data.message || `Sunucu hatası (${response.status})`;
          throw new Error(errorMsg);
        }
      } catch (fetchError) {
        console.error("Verify fetch error:", fetchError);
        // Re-throw if it's already a meaningful error
        if (fetchError.message && fetchError.message !== "Failed to fetch") {
          throw fetchError;
        }
        throw new Error(t("common:serverConnectionFailed"));
      }

      // Save token to localStorage
      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }

      // If there's an onSuccess callback, call it and let the parent handle navigation
      if (onSuccess) {
        onSuccess(data);
        onClose();
        return;
      }

      toast.success(registrationData ? t("auth:registerSuccessWelcome") : t("auth:loginSuccess"));
      onClose();
      
      // Redirect based on user status
      if (data.is_new_user || registrationData) {
        // New registration - check if business
        if (data.user?.role === "business" || registrationData?.role === "business") {
          navigate("/register-business", { state: { user: data.user, token: data.token, firstTime: true } });
        } else {
          navigate("/", { state: { user: data.user } });
        }
      } else if (data.user?.role === "business") {
        // Check if business user has a business
        try {
          const bizResponse = await fetch(`${API}/my-businesses`, {
            credentials: "include",
          });
          
          if (bizResponse.ok) {
            const businesses = await bizResponse.json();
            
            if (!businesses || businesses.length === 0) {
              navigate("/register-business", { state: { user: data.user, firstTime: true } });
              return;
            }
          }
        } catch (e) {
          console.error("Error checking businesses:", e);
        }
        navigate("/", { state: { user: data.user } });
      } else {
        navigate("/", { state: { user: data.user } });
      }
    } catch (error) {
      console.error("Verify Error:", error);
      
      let errorMessage = t("common:verificationFailed");
      
      if (error.code === "auth/invalid-verification-code") {
        errorMessage = t("admin:invalidVerificationCode");
      } else if (error.code === "auth/code-expired") {
        errorMessage = t("admin:verificationCodeExpired");
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0) return;
    setStep("phone");
    setOtp("");
    setConfirmationResult(null);
  };

  const handleClose = () => {
    setStep("phone");
    setPhoneNumber("");
    setOtp("");
    setConfirmationResult(null);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === "otp" ? (
              <ShieldCheck className="w-5 h-5 text-green-600" />
            ) : (
              <Phone className="w-5 h-5 text-navy" />
            )}
            {step === "phone" ? t("common:phoneLogin") : t("admin:verificationCode")}
          </DialogTitle>
          <DialogDescription>
            {step === "phone"
              ? t("admin:enterPhoneDesc")
              : `${formatPhoneNumber(phoneNumber)} numarasına gönderilen 6 haneli kodu girin.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {step === "phone" ? (
            <>
              <div>
                <Label htmlFor="phone">{t("common:telefonNumarasi")}</Label>
                <div className="flex gap-2 mt-1">
                  <div className="flex items-center px-3 bg-grey-100 border rounded-xl text-sm font-medium">
                    +90
                  </div>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="5XX XXX XX XX"
                    value={phoneNumber}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "");
                      // Remove leading 0 if present
                      if (value.startsWith("0")) {
                        value = value.slice(1);
                      }
                      setPhoneNumber(value.slice(0, 10));
                    }}
                    className="h-11 rounded-xl flex-1"
                    data-testid="phone-auth-input"
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-grey mt-1">
                  Örnek: 532 123 45 67
                </p>
              </div>

              <div id="recaptcha-container" ref={recaptchaContainerRef}></div>

              <Button
                onClick={handleSendOtp}
                className="w-full h-11 bg-navy hover:bg-navy/90 rounded-xl"
                disabled={loading || phoneNumber.length < 10}
                data-testid="send-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Gönderiliyor...
                  </>
                ) : (
                  t("admin:sendVerificationCode")
                )}
              </Button>
            </>
          ) : (
            <>
              <div>
                <Label htmlFor="otp">{t("admin:verificationCode")}</Label>
                <Input
                  id="otp"
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl mt-1"
                  data-testid="otp-input"
                  disabled={loading}
                />
              </div>

              <Button
                onClick={handleVerifyOtp}
                className="w-full h-11 bg-navy hover:bg-navy/90 rounded-xl"
                disabled={loading || otp.length !== 6}
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Doğrulanıyor...
                  </>
                ) : (
                  t("common:verifyAndLogin")
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setStep("phone");
                    setOtp("");
                  }}
                  className="flex items-center gap-1 text-navy hover:underline"
                  disabled={loading}
                >
                  <ArrowLeft className="w-4 h-4" />
                  Geri Dön
                </button>

                <button
                  type="button"
                  onClick={handleResendOtp}
                  disabled={resendCooldown > 0 || loading}
                  className={`flex items-center gap-1 ${
                    resendCooldown > 0 ? "text-grey" : "text-navy hover:underline"
                  }`}
                >
                  <RefreshCw className="w-4 h-4" />
                  {resendCooldown > 0 ? `${resendCooldown}s` : t("auth:resend")}
                </button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
